<template>
  <div>
    <div v-if="leadTime.chartData" class="dep-gr">
      <div class="child1">
        <h2>LeadTime / Month</h2>
        <div class="leadTimeContainer">
          <BarChart
            :chart-data="leadTime.chartData"
            :chart-options="leadTime.chartOptions"
          />
        </div>
        <div class="selectContainer">
          <select class="sl" v-model="currentDivisionSelected">
            <option
              v-for="(division, index) in MetricsConst.DIVISIONS"
              :key="index"
              >{{ division }}</option
            >
          </select>
          <div class="bt" @click="exportToFileCsv()">Export</div>
        </div>
      </div>
    </div>
    <ReloadIcon v-else></ReloadIcon>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import MetricsConst from '@/views/Metrics/common/MetricsConst.js';
import 'vue2-datepicker/index.css';
import BarChart from '@/components/Metrics/BarChart.vue';
import ReloadIcon from '@/views/Metrics/ReloadIcon.vue';
const { mapState, mapActions } = createNamespacedHelpers('metrics');
import {
  showFiniteNumberOfDigit,
  exportToFileCsv
} from '@/views/Metrics/common/utils.js';
export default {
  components: {
    BarChart,
    ReloadIcon
  },
  data() {
    return {
      leadTime: {
        leadTimeData: null,
        chartData: null,
        labels: null,
        datasets: null,
        maxMonth: 0
      },
      MetricsConst: MetricsConst,
      leadTimeCache: {
        valueKey: 'LEAD_TIME_CACHE',
        durationInSeconds: 3600 * 24
      },
      releaseNoteDataApi: null,
      currentDivisionSelected: MetricsConst.DIVISIONS[0]
    };
  },
  computed: {
    ...mapState(['releaseNoteData'])
  },
  props: ['releasesOnProdData', 'fontSize'],
  methods: {
    ...mapActions(['getReleaseNote']),
    renderLeadTimeData: async function() {
      let datasets = [],
        maxMonth = 0,
        releaseNoteData = this.releaseNoteDataApi;
      let result = {};
      for (let division in releaseNoteData) {
        for (let date in releaseNoteData[division]) {
          result[date] = {};
          for (let division of MetricsConst.DIVISIONS) {
            result[date][division] = 0;
          }
          maxMonth = Math.max(
            maxMonth,
            releaseNoteData[division][date].averageDiffDays
          );
        }
      }
      for (let division in releaseNoteData) {
        for (let date in releaseNoteData[division]) {
          result[date][division] = showFiniteNumberOfDigit(
            releaseNoteData[division][date].averageDiffDays,
            2
          );
        }
      }
      let labels = Object.keys(result).sort((a, b) => {
        let dateA = new Date(a),
          dateB = new Date(b);
        if (dateA > dateB) {
          return 1;
        }
        if (dateA < dateB) {
          return -1;
        }
        return 0;
      });
      for (let division of MetricsConst.DIVISIONS) {
        let dataOfDivision = [];
        for (let date of labels) {
          dataOfDivision.push(result[date][division]);
        }

        datasets.push({
          type: 'bar',
          label: division,
          data: dataOfDivision,
          backgroundColor: this.MetricsConst.COLORS[division],
          datalabels: {
            anchor: 'end',
            align: 'end',
            font: {
              size: 18,
              family: 'Verdana'
            },
            color: 'rgba(0, 0, 0, 0.7)',
            formatter: function(value) {
              return value === 0 ? '' : value;
            }
          },
          borderWidth: 1,
          pointStyle: 'rectRot',
          pointRadius: 5,
          pointBorderColor: 'rgb(0, 0, 0)'
        });
      }
      this.leadTime.chartData = {
        datasets: datasets,
        labels: labels
      };
      this.leadTime.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              font: {
                size: 15,
                family: 'Verdana'
              }
            }
          },
          y: {
            ticks: {
              font: {
                size: 15,
                family: 'Verdana'
              }
            },
            beginAtZero: true,
            max: Math.round(maxMonth) + 2
          }
        },
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 20,
              family: 'Verdana'
            }
          },
          legend: {
            labels: {
              usePointStyle: true,
              font: {
                size: 14,
                family: 'Verdana'
              }
            },
            position: 'bottom'
          }
        }
      };
    },
    releaseNoteCache: async function() {
      localStorage.getItem(this.leadTimeCache.valueKey);
      let resultCache = JSON.parse(
        localStorage.getItem(this.leadTimeCache.valueKey)
      );
      if (resultCache && resultCache.saveDate && resultCache.releaseNoteData) {
        const date1 = new Date(resultCache.saveDate),
          date2 = new Date(),
          diffInSeconds = Math.abs(date2 - date1) / 1000;
        if (diffInSeconds >= this.leadTimeCache.durationInSeconds) {
          localStorage.removeItem(this.leadTimeCache.valueKey);
          return this.releaseNoteCache();
        }
        return resultCache.releaseNoteData;
      } else {
        await this.getReleaseNote();
        localStorage.setItem(
          this.leadTimeCache.valueKey,
          JSON.stringify({
            saveDate: Date.now(),
            releaseNoteData: this.releaseNoteData
          })
        );
        return this.releaseNoteData;
      }
    },
    exportToFileCsv: function() {
      let data = this.releaseNoteDataApi[this.currentDivisionSelected];
      exportToFileCsv(data, this.currentDivisionSelected);
    }
  },
  watch: {
    fontSize: function() {
      this.leadTime?.chartData?.datasets.forEach(element => {
        element.datalabels.font.size = this.fontSize;
      });
    }
  },
  mounted: async function() {
    this.releaseNoteDataApi = await this.releaseNoteCache();
    await this.renderLeadTimeData();
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@800');
.dep-gr {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  width: 100%;
}
.child1 {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  width: 100%;
}
.dep-gr .child1 {
  padding: 30px 50px;
  border-radius: 2px;
  box-shadow: rgba(0, 0, 0, 0.16) 0 1px 4px;
  margin-top: 30px;
}
.dep-gr h2 {
  font-family: 'Nunito Sans', sans-serif;
  opacity: 0.8;
}
.sl {
  padding: 0 15px;
  margin: 10px 30px 0 0;
  background-color: rgb(68, 80, 105, 0.7);
  color: white;
  border-radius: 2px;
  font-size: 20px;
}
.bt {
  padding: 2px 15px;
  margin: 10px 20px 0 0;
  cursor: pointer;
  background-color: #252b48;
  color: white;
  border-radius: 2px;
  font-size: 20px;
  transition-duration: 350ms;
}
@media (min-width: 500px) and (max-width: 700px) {
  .sl {
    font-size: 15px;
  }
  .bt {
    font-size: 15px;
  }
}
@media (max-width: 499px) {
  .sl {
    font-size: 12px;
  }
  .bt {
    font-size: 12px;
  }
}
.bt:hover {
  opacity: 0.7;
}

.leadTimeContainer {
  width: 100% !important;
  height: 100% !important;
}

.selectContainer {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: 30px;
}
</style>
